import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RarityBadge from '../components/common/RarityBadge';
import RarityAvatar from '../components/common/RarityAvatar';
import { 
  seedTypes, 
  formatGrowTime,
  calculateBpPerMinute, 
  getFilteredSeeds, 
  getSeedRarities, 
  rarities 
} from '../utils/seedUtils';
import { typography, flex } from '../theme/typography';

const Seeds = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();

  const filteredSeeds = getFilteredSeeds(tabValue, searchQuery, seedTypes) || [];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchQuery(''); // Reset search when changing tabs
  };

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      py: 0,
      gap: 0
    }}>
      {/* Tabs container */}
      <Box sx={{ width: 1200, mx: 'auto' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            width: 1200,
            '& .MuiTabs-flexContainer': {
              justifyContent: 'space-evenly',
              gap: 0,
            },
            '& .MuiTab-root': {
              minWidth: 400,
              maxWidth: 400,
              width: 400,
              fontWeight: 500,
              minHeight: 80,
              height: 80,
              maxHeight: 80,
              borderRadius: 0,
              boxShadow: 'none',
              background: 'none',
              border: 'none',
              '&.Mui-selected': {
                color: '#388e3c',
                fontWeight: 600,
                background: 'none',
                border: 'none',
                boxShadow: 'none',
              },
              '&:hover': {
                background: 'none',
              },
              '&.Mui-focusVisible': {
                background: 'none',
                outline: 'none',
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
          aria-label="seed database"
        >
          {seedTypes.map((type) => (
            <Tab
              key={type.id}
              label={
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  width: '100%',
                  py: 0.5,
                  minHeight: 80,
                  height: 80,
                  maxHeight: 80,
                }}>
                  <Box sx={{ ...typography.h3, fontSize: '2.5rem', height: 40, width: 40, ...flex.center }}>
                    {type.emoji}
                  </Box>
                  <Typography sx={{ ...typography.label, whiteSpace: 'nowrap' }}>
                    {type.name}
                  </Typography>
                </Box>
              }
              sx={{ minWidth: 400, maxWidth: 400, width: 400, height: 80 }}
              aria-label={`View ${type.name} seeds`}
            />
          ))}
        </Tabs>
      </Box>
      {/* Search bar container */}
      <Box sx={{ width: 1200, mx: 'auto', mb: 3, height: 40, display: 'flex', alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Search all seeds..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            style: {
              height: 40,
              minHeight: 40,
              maxHeight: 40,
              display: 'flex',
              alignItems: 'center',
              textAlign: 'center',
              justifyContent: 'center',
            },
            inputProps: {
              style: {
                textAlign: 'center',
              }
            }
          }}
          sx={{
            height: 40,
            minHeight: 40,
            maxHeight: 40,
            borderRadius: theme.shape.borderRadius,
            '& .MuiOutlinedInput-root': {
              height: 40,
              minHeight: 40,
              maxHeight: 40,
              display: 'flex',
              alignItems: 'center',
              fontSize: '1rem',
              boxSizing: 'border-box',
              padding: 0,
              borderRadius: theme.shape.borderRadius,
              textAlign: 'center',
              justifyContent: 'center',
            },
            '& input': {
              height: 40,
              minHeight: 40,
              maxHeight: 40,
              display: 'flex',
              alignItems: 'center',
              fontSize: '1rem',
              boxSizing: 'border-box',
              padding: 0,
              borderRadius: theme.shape.borderRadius,
              textAlign: 'center',
              justifyContent: 'center',
            },
            '& input::placeholder': {
              textAlign: 'center',
            },
          }}
        />
      </Box>
      {/* Seed content container */}
      <Box sx={{ width: 1200, mx: 'auto' }}>
        {filteredSeeds.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography sx={{ ...typography.body, color: 'text.secondary' }}>
              {searchQuery
                ? `No seeds found matching "${searchQuery}"`
                : 'No seeds available in this category'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <Grid container spacing={2} sx={{
                display: 'flex',
                alignItems: 'stretch',
                width: 1200,
                margin: '0 auto',
                justifyContent: 'center',
              }}>
                {filteredSeeds.map((seed) => {
                  const seedRarities = getSeedRarities(seed);

                  return (
                    <Box key={seed.name} sx={{ mb: 4, display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'center', alignItems: 'stretch', width: '100%' }}>
                      {/* Seed Image Card */}
                      <Box sx={{ 
                        minWidth: 100, 
                        maxWidth: 100, 
                        width: 100,
                        height: 128,
                        flex: '0 0 auto', 
                        display: 'flex',
                        '&:hover': {
                          transform: 'none !important',
                          boxShadow: 'none !important'
                        }
                      }}>
                        <Card elevation={0} sx={{ 
                          height: '100%', 
                          width: '100%', 
                          display: 'flex', 
                          flexDirection: 'column', 
                          bgcolor: 'transparent', 
                          boxShadow: 'none', 
                          border: 'none',
                          transform: 'none !important',
                          transition: 'none !important',
                          '&:hover': {
                            transform: 'none !important',
                            boxShadow: 'none !important'
                          }
                        }}>
                          <CardContent sx={{
                            p: 0, // Remove padding to maximize space
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100px',
                            minHeight: '100px',
                          }}>
                            <Box sx={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                              <Typography 
                                sx={{
                                  ...typography.bodyBold,
                                  px: 1,
                                  textAlign: 'center',
                                  width: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1.2,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {seed.name}
                              </Typography>
                            </Box>
                            <Box sx={{ m: 0, p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <RarityAvatar
                                src={seed.imagePath || `/images/seeds/${seed.id || seed.name.toLowerCase().replace(/\s+/g, '_')}/common/preview.png`}
                                alt={seed.name}
                                size={90}
                                sx={{
                                  borderRadius: 1,
                                  boxShadow: 2,
                                  '& img': {
                                    mixBlendMode: 'multiply',
                                    backgroundColor: 'transparent'
                                  }
                                }}/>
                            </Box>
                          </CardContent>
                        </Card>
                      </Box>
                      {/* Rarity Cards */}
                      {rarities.map(rarity => {
                        const rarityData = seedRarities[rarity];
                        if (!rarityData) return null;
                        return (
                          <Box key={`${seed.name}-${rarity}`} sx={{ 
                            minWidth: 130, 
                            maxWidth: 130, 
                            width: 130,
                            height: '100%',
                            flex: '0 0 auto', 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            '&:hover': {
                              transform: 'none !important',
                              boxShadow: 'none !important'
                            }
                          }}>
                            <Card elevation={0} sx={{ 
                              height: '100%', 
                              width: '100%', 
                              bgcolor: 'transparent', 
                              boxShadow: 'none',
                              transform: 'none !important',
                              transition: 'none !important',
                              '&:hover': {
                                transform: 'none !important',
                                boxShadow: 'none !important',
                                backgroundColor: 'transparent !important'
                              }
                            }}>
                              <CardContent sx={{
                                p: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                height: '100%',
                                minHeight: '100%',
                                '&:last-child': {
                                  pb: 0
                                }
                              }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 1 }}>
                                  <RarityBadge rarity={rarity} size="small" />
                                </Box>
                                <Box sx={{ width: '100%', textAlign: 'center' }}>
                                  <Typography sx={{ ...typography.body, textAlign: 'center' }}>
                                    {formatGrowTime(rarityData.growTime)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    {rarityData.biopoints} BP
                                  </Typography>
                                  <Typography variant="caption" color="primary" display="block" sx={{ fontWeight: 600, mt: 0.5 }}>
                                    {calculateBpPerMinute(rarityData.biopoints, rarityData.growTime).toFixed(2)} BP/min
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Grid>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Seeds;
